import { makeStyles } from '@material-ui/core';
import { FormikProvider, useFormik, useFormikContext } from 'formik';
import { isEmpty, omit } from 'lodash';
import React, { ChangeEvent, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';
import { translate } from '../../../../../common/intl';
import { getHasPermission } from '../../../../../common/utils/selectors';
import { Permission } from '../../../../../state/ducks/auth/types';
import { GeneralSettings, WorkOrderLotRequirement } from '../../../../../state/ducks/company/types';
import { documentTypeSelectors } from '../../../../../state/ducks/documentRevisions/documentType';
import { DOC_TYPE_GROUP } from '../../../../../state/ducks/documentRevisions/documentType/types';
import { CellTemplateProps, ColumnDefinition, EditableItem } from '../../../../components/KendoDataGrid/KendoDataGrid.types';
import { MODE_FIELD, Mode } from '../../../../components/KendoDataGrid/constants';
import { Autocomplete, FormikField } from '../../../../components/forms/fields-next';
import { OptionType } from '../../../../components/forms/fields/Autocomplete/types';
import SettingsPanel from '../../components/SettingsPanel';
import SettingsTable from '../../components/SettingsTable';
import ActionsCellTemplate from '../../components/SettingsTable/components/ActionsCellTemplate';
import SwitchControl from '../../components/SwitchControl';
import { SettingsPanelProps } from '../../types';

const SETTINGS_FIELD = 'workOrderLotRequirements';

const useStyles = makeStyles({
  partTypeAutocomplete: {
    width: '100%',
  },
  switchControlRoot: {
    '&&': {
      flexDirection: 'row',
      justifyContent: 'flex-start',
    },
  },
});

interface WorkOrderLotRequirementRow extends WorkOrderLotRequirement, EditableItem {
  id: string
}

interface PartTypeCellProps extends CellTemplateProps<WorkOrderLotRequirementRow> {
  options: OptionType[]
  availableOptions: OptionType[]
}

const PartTypeCell: React.FC<PartTypeCellProps> = ({ dataItem, field, options, availableOptions }) => {
  const classes = useStyles();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { values, setFieldValue, errors } = useFormikContext<WorkOrderLotRequirementRow>();
  const isEditMode = [Mode.add, Mode.edit].includes(dataItem[MODE_FIELD]);
  const currentValue = isEditMode
    ? values.documentTypeId
    : dataItem.documentTypeId;
  const selectedOption = options.find((option) => option.value === currentValue) ?? null;

  if (!isEditMode) {
    return <div>{selectedOption?.label ?? ''}</div>;
  }

  return (
    <FormikField name={field} error={Boolean(errors.documentTypeId)}>
      <Autocomplete
        options={availableOptions}
        className={classes.partTypeAutocomplete}
        value={selectedOption}
        getOptionLabel={(option: OptionType) => option.label}
        getOptionSelected={(option: OptionType, value: OptionType) => option.value === value.value}
        onChange={(_event: ChangeEvent<unknown>, option: OptionType | null) => setFieldValue(field, option?.value ?? '')}
        placeholder={translate('common.select')}
        disableClearable
        size="small"
        data-cy="work-order-part-type"
      />
    </FormikField>
  );
};

type RequirementCellProps = CellTemplateProps<WorkOrderLotRequirementRow>;

const RequirementCell: React.FC<RequirementCellProps> = ({ dataItem, field }) => {
  const classes = useStyles();
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { values, setFieldValue } = useFormikContext<WorkOrderLotRequirementRow>();
  const isEditMode = [Mode.add, Mode.edit].includes(dataItem[MODE_FIELD]);
  const required = (isEditMode
    ? values.requireProductLotDuringWorkOrderExecution
    : dataItem.requireProductLotDuringWorkOrderExecution) ?? true;

  return (
    <SwitchControl
      checked={Boolean(required)}
      disabled={!isEditMode}
      onChange={(_event: ChangeEvent<unknown>, checked: boolean) => setFieldValue(field, checked)}
      inputProps={{ 'aria-label': translate('administration.general.settings.work.order.execution.require.product.lot') }}
      labelProps={{ className: classes.switchControlRoot }}
      label=""
    />
  );
};

const WorkOrderLotRequirements: React.FC<SettingsPanelProps> = (props) => {
  // eslint-disable-next-line @typescript-eslint/unbound-method
  const { setFieldValue, values } = useFormikContext<GeneralSettings>();
  const hasPermission = useSelector(getHasPermission(Permission.COMPANY_UPDATE_LOCATIONS));
  const documentTypes = useSelector(documentTypeSelectors.getDocumentTypeList);
  const rows = values.workOrderLotRequirements ?? [];
  const [editedRow, setEditedRow] = useState<WorkOrderLotRequirementRow>();
  const [previousRow, setPreviousRow] = useState<WorkOrderLotRequirementRow>();

  const partDocumentTypes = useMemo(
    () => documentTypes
      .filter((documentType) => documentType.active && documentType.group === DOC_TYPE_GROUP.PART)
      .map((documentType) => ({
        label: `${documentType.documentTypeName} (${documentType.documentTypeAcronym.replace('-', '')})`,
        value: documentType.id,
      })),
    [documentTypes],
  );

  const formik = useFormik<WorkOrderLotRequirementRow>({
    initialValues: {
      id: '',
      documentTypeId: '',
      requireProductLotDuringWorkOrderExecution: true,
      [MODE_FIELD]: Mode.show,
    },
    validate: (formValues) => (isEmpty(formValues.documentTypeId)
      ? { documentTypeId: 'validators.required' }
      : {}),
    onSubmit: (formValues) => {
      const row = omit(formValues, [MODE_FIELD, 'id']) as WorkOrderLotRequirement;
      const previousIndex = editedRow?.[MODE_FIELD] === Mode.edit
        ? rows.findIndex((item) => item.documentTypeId === previousRow?.documentTypeId)
        : -1;
      const updatedRows = previousIndex >= 0
        ? rows.map((item, index) => index === previousIndex ? row : item)
        : [...rows, row];

      setFieldValue(SETTINGS_FIELD, updatedRows);
      setEditedRow(undefined);
      setPreviousRow(undefined);
    },
  });
  const beginAdd = () => {
    const newRow: WorkOrderLotRequirementRow = {
      id: uuidv4(),
      documentTypeId: '',
      requireProductLotDuringWorkOrderExecution: true,
      [MODE_FIELD]: Mode.add,
    };
    formik.setValues(newRow);
    setEditedRow(newRow);
    setPreviousRow(undefined);
  };

  const beginEdit = (row: WorkOrderLotRequirementRow) => {
    formik.setValues({ ...row, [MODE_FIELD]: Mode.edit });
    setPreviousRow(row);
    setEditedRow({ ...row, [MODE_FIELD]: Mode.edit });
  };

  const discardEdit = () => {
    setEditedRow(undefined);
    setPreviousRow(undefined);
  };

  const deleteRow = (row: WorkOrderLotRequirementRow) => {
    const updatedRows = rows.filter((item) => item.documentTypeId !== row.documentTypeId);
    setFieldValue(SETTINGS_FIELD, updatedRows);
  };

  const selectedDocumentTypeIds = rows
    .filter((row) => row.documentTypeId !== previousRow?.documentTypeId)
    .map((row) => row.documentTypeId);
  const availableOptions = partDocumentTypes.filter(
    (option) => option.value === editedRow?.documentTypeId || !selectedDocumentTypeIds.includes(option.value),
  );

  const data: WorkOrderLotRequirementRow[] = [
    ...rows.map((row) => ({
      ...row,
      id: row.documentTypeId,
      [MODE_FIELD]: editedRow?.documentTypeId === row.documentTypeId ? Mode.edit : Mode.show,
    })),
    ...(editedRow?.[MODE_FIELD] === Mode.add ? [editedRow] : []),
  ];

  const schema: Array<ColumnDefinition<WorkOrderLotRequirementRow>> = [
    {
      id: 'documentTypeId',
      field: 'documentTypeId',
      title: 'administration.general.settings.work.order.execution.part.type',
      template: (cellProps) => (
        <PartTypeCell
          {...cellProps}
          options={partDocumentTypes}
          availableOptions={availableOptions}
        />
      ),
    },
    {
      id: 'requireProductLotDuringWorkOrderExecution',
      field: 'requireProductLotDuringWorkOrderExecution',
      title: 'administration.general.settings.work.order.execution.require.product.lot',
      template: (cellProps) => <RequirementCell {...cellProps} />,
    },
    {
      id: 'actions',
      field: 'actions',
      title: 'common.actions',
      width: 100,
      template: (cellProps) => (
        <ActionsCellTemplate
          {...cellProps}
          isActive={hasPermission}
          onEdit={beginEdit}
          onDelete={hasPermission ? deleteRow : undefined}
          onDiscard={discardEdit}
          onConfirm={async () => {
            await formik.submitForm();
          }}
        />
      ),
    },
  ];

  return (
    <SettingsPanel
      {...props}
      title={translate('administration.general.settings.work.order.execution')}
      onAddNew={hasPermission && !editedRow ? beginAdd : undefined}
    >
      <FormikProvider value={formik}>
        <SettingsTable
          isActive={hasPermission}
          data={data}
          isEditing={Boolean(editedRow)}
          schema={schema}
          addButtonLabel={translate('common.add.new')}
          onAdd={beginAdd}
        />
      </FormikProvider>
    </SettingsPanel>
  );
};

export default WorkOrderLotRequirements;
